/* components/patterns/integrations/BackfillProgress.jsx — «История данных»: помесячная
   карта исторического бэкфилла подключения. Общий для Настроек → Интеграции
   (BusinessIntegrationsTab) и мастера онбординга (шаг «Загрузка данных»).

   Чисто презентационный: поллинг/загрузку данных держит вызывающий и передаёт data.
   Контракт data (GET /api/integrations/backfill/, заморожен): {active, preparing?,
   blocked?, months:[{period, status, rows, purchases_error, error}], summary:{total,
   done, empty, failed, pending, running, date_min}}; months идут от новых к старым,
   period = 'YYYY-MM-01'. data=null / карта пуста без активной загрузки и без причины
   → блок не рендерится.

   blocked — {code, tag, right, message} | null: карты нет и сама она НЕ появится (у
   API-пользователя нет права на отчёты по продажам). Приходит с active:false — опрос
   останавливается, а блок обязан остаться на экране и назвать причину: иначе человек
   смотрит на вечное «Готовим полную историю…» у подключения, которое не загрузит
   ничего (прод 18.09.2026). Ответ старого бэкенда без ключа = причины нет.

   pure-хелперы (groupBackfillMonths/backfillStatusText/backfillCellTitle/
   backfillBlocked/backfillVisible) экспортированы для vitest — см.
   screens/settings/BusinessIntegrationsTab.test.js (реэкспорт там же). */

import { Icon } from '../../ui/Icon.jsx';

  const BF_MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  // родительный падеж — для «История загружена: с марта 2023»
  const BF_MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  const BF_CELL_STATUS = {
    done: 'загружен',
    running: 'загружается',
    pending: 'в очереди',
    empty: 'на сервере пусто',
    failed: 'ошибка',
  };

  // Группировка месяцев по годам для сетки: строка = год (новые сверху), 12 позиций
  // янв..дек; null — месяц вне карты (до начала истории / будущее), держит колонку.
  // Экспортируется для vitest (чистая логика без React).
  function groupBackfillMonths(months) {
    const byYear = new Map();
    for (const m of months || []) {
      const parts = String((m && m.period) || '').split('-');
      const year = Number(parts[0]);
      const idx = Number(parts[1]) - 1;
      if (!Number.isFinite(year) || !(idx >= 0 && idx <= 11)) continue;
      if (!byYear.has(year)) byYear.set(year, new Array(12).fill(null));
      byYear.get(year)[idx] = m;
    }
    return Array.from(byYear.keys()).sort((a, b) => b - a)
      .map((year) => ({ year, cells: byYear.get(year) }));
  }

  // Причина, по которой история не загрузится сама, или null. Причиной считаем
  // только объект с непустым message: мусор в поле (строка, пустой объект, ответ
  // старого бэкенда без ключа) не должен ни показать пустую красную плашку, ни
  // спрятать «Готовим…».
  function backfillBlocked(data) {
    const b = data && data.blocked;
    return b && typeof b === 'object' && typeof b.message === 'string' && b.message
      ? b : null;
  }

  // Рендерить ли блок вообще. Одно правило на двоих: им пользуется и сам блок, и
  // обёртка-якорь в BusinessIntegrationsTab (раньше условие было продублировано
  // в двух местах и обязано было совпадать). blocked приходит с active:false и
  // пустой картой — без третьего условия блок исчез бы ровно тогда, когда ему
  // есть что сказать.
  function backfillVisible(data) {
    if (!data) return false;
    return ((data.months || []).length > 0) || data.active === true
      || !!backfillBlocked(data);
  }

  // Строка над сеткой: пока active — «Загружено X из N месяцев» (X = done+empty:
  // пустой месяц тоже «пройден»); после завершения — «История загружена: с марта
  // 2023» (месяц из summary.date_min) + « · ошибок: N» при failed>0.
  function backfillStatusText(data) {
    const s = (data && data.summary) || {};
    const total = s.total || 0;
    // blocked: карты нет и не будет, пока человек не выдаст право, — текст причины
    // приходит с бэкенда целиком (в нём название и тег права из того же каталога,
    // что рисует чек-лист прав). Карта, если она есть, важнее причины.
    const blocked = backfillBlocked(data);
    if (blocked && total === 0) return blocked.message;
    // preparing: карты ещё нет (первые секунды после активации / ночная догрузка
    // впереди) — поллинг продолжается, сетка появится сама.
    if (data && data.preparing === true && total === 0) {
      return 'Готовим полную историю данных — месяцы появятся здесь автоматически';
    }
    if (data && data.active === true) {
      return 'Загружено ' + ((s.done || 0) + (s.empty || 0)) + ' из ' + total + ' месяцев';
    }
    let text = 'История загружена';
    const dm = typeof s.date_min === 'string' ? s.date_min.split('-') : null;
    const mi = dm ? Number(dm[1]) - 1 : -1;
    if (dm && mi >= 0 && mi <= 11) text += ': с ' + BF_MONTHS_GEN[mi] + ' ' + dm[0];
    if (s.failed > 0) text += ' · ошибок: ' + s.failed;
    return text;
  }

  // Строка про историю ДВИЖЕНИЯ товара (ключ `movement` ответа, 29.09.2026) — или
  // null. Её месяцы грузятся своим шагом и позже продаж, поэтому «История
  // загружена» о продажах ничего не говорила о движении: карта объявляла готовность,
  // пока вкладка «Движение» видела 45 дней. Пока загрузка идёт — прогресс; когда
  // закончилась, но часть месяцев так и не загрузилась (попытки исчерпаны), —
  // сказать об этом, а не молчать. `unavailable` (нет права на «Проводки», у
  // r_keeper — StoreHouse) — строки нет: причину называет чек-лист прав рядом, а
  // «загружается» было бы обещанием, которое не сбудется. Ответ старого бэкенда без
  // ключа — строки нет.
  function backfillMovementText(data) {
    const mv = data && data.movement;
    if (!mv || typeof mv !== 'object' || mv.unavailable === true) return null;
    const total = mv.total || 0;
    // «из 21 месяца», «из 104 месяцев»: после «из» — родительный падеж
    const unit = (total % 10 === 1 && total % 100 !== 11) ? 'месяца' : 'месяцев';
    if (mv.active === true) {
      let text = 'Движение товара: загружено ' + (mv.loaded || 0) + ' из ' + total + ' ' + unit;
      if (mv.errors > 0) text += ' · с ошибкой: ' + mv.errors + ' (повторим сами)';
      return text;
    }
    if (mv.errors > 0) {
      return 'Движение товара: не загрузилось за ' + mv.errors + ' из ' + total + ' ' + unit
        + ' — касса не отдала данные';
    }
    return null;
  }

  // Опрашивать ли карту дальше. Продажи (`active`) и движение (`movement.active`)
  // грузятся разными шагами: карта продаж может быть готова, а движение — ещё
  // идти, и без второго условия строка «Движение товара: загружено 3 из 104»
  // замирала бы до перезагрузки страницы.
  function backfillPolling(data) {
    if (!data) return false;
    return data.active === true
      || !!(data.movement && typeof data.movement === 'object' && data.movement.active === true);
  }

  // title ячейки: «янв 2024 — статус»; у failed — причина из error (сама ячейка
  // в danger-тоне), у done с purchases_error — пометка про недоехавшие закупки.
  function backfillCellTitle(m, idx, year) {
    let t = BF_MONTHS_SHORT[idx] + ' ' + year + ' — ' + (BF_CELL_STATUS[m.status] || m.status);
    if (m.status === 'failed' && m.error) t += ': ' + m.error;
    if (m.status === 'done' && m.purchases_error) t += ' · закупки не загрузились';
    return t;
  }

  // Блок «История данных»: прогресс-бар + сетка «год × 12 месяцев» + строка статуса.
  // Условие видимости внутри: карты нет и загрузка не идёт/не ожидается (демо-
  // подключение) — блока просто нет (фоновая информация, без тостов).
  function BackfillProgress({ data }) {
    const months = (data && data.months) || [];
    const summary = (data && data.summary) || {};
    const active = !!(data && data.active === true);
    // preparing приходит с active:true, так что поллинг вызывающего не обрывается
    if (!backfillVisible(data)) return null;
    const total = summary.total || 0;
    const loaded = (summary.done || 0) + (summary.empty || 0);
    const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
    const rows = groupBackfillMonths(months);
    // причина показывается, только пока карты нет: загруженную историю за
    // сообщением о правах не прячем (то же правило — в backfillStatusText)
    const blocked = total === 0 ? backfillBlocked(data) : null;
    const movementText = backfillMovementText(data);

    return (
      <div className="backfill stack stack-8">
        <h4 className="t-h5" style={{ margin: 0 }}>История данных</h4>
        {active && total > 0 && (
          <div className="backfill__bar" role="progressbar"
            aria-valuemin={0} aria-valuemax={total} aria-valuenow={loaded}>
            <span className="backfill__bar-fill" style={{ width: pct + '%' }} />
          </div>
        )}
        {blocked && (
          <p className="backfill__blocked t-body-sm" role="status">
            <Icon name="x-circle" size={16} className="backfill__blocked-ic" />
            <span>{blocked.message}</span>
          </p>
        )}
        {!blocked && (active || total > 0) && (
          <p className="backfill__note t-caption text-mute">{backfillStatusText(data)}</p>
        )}
        {!blocked && movementText && (
          <p className="backfill__note t-caption text-mute">{movementText}</p>
        )}
        {/* сетка — только когда в ней есть строки: пустой контейнер добавлял бы
            лишний gap стека под текстом «Готовим…» / под причиной */}
        {rows.length > 0 && (
        <div className="backfill__scroll">
          <div className="backfill__grid">
            {rows.map(({ year, cells }) => (
              <div key={year} className="backfill__row">
                <span className="backfill__year t-caption text-mute">{year}</span>
                <div className="backfill__cells">
                  {cells.map((m, i) => (m
                    ? (
                      <span key={i} className={'backfill__cell is-' + m.status}
                        title={backfillCellTitle(m, i, year)}>{BF_MONTHS_SHORT[i]}</span>
                    )
                    : (
                      <span key={i} className="backfill__cell is-void" aria-hidden="true">
                        {BF_MONTHS_SHORT[i]}
                      </span>
                    )))}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    );
  }

export { BackfillProgress, groupBackfillMonths, backfillStatusText, backfillCellTitle,
  backfillBlocked, backfillVisible, backfillMovementText, backfillPolling };
